import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Star, Sparkles } from 'lucide-react';
import { PawBackground } from './PawBackground';
const subjects = ['pet', 'plushie', 'stuffed animal', 'action figure'];
export function Hero() {
  const [subjectIndex, setSubjectIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setSubjectIndex(prev => (prev + 1) % subjects.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  return <section className="relative pt-24 pb-20 md:pt-48 md:pb-32 overflow-hidden" data-id="element-99" data-mp="xaxgvsxqzynp">
      {/* Twinkling paw prints */}
      <PawBackground data-id="element-100" data-mp="sde28rx51jv8" />

      {/* Decorative background blobs */}
      <div className="absolute top-20 left-[-10%] w-[40rem] h-[40rem] bg-coral/20 blob-shape -z-10 blur-3xl" data-id="element-101" data-mp="nb6n5th0hnoo" />
      <div className="absolute bottom-[-10%] right-[-5%] w-[35rem] h-[35rem] bg-lavender/20 blob-shape -z-10 blur-3xl" style={{
      animationDelay: '-4s'
    }} data-id="element-102" data-mp="3d33gb2q0p1mv" />

      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center" data-id="element-103" data-mp="3j9tymsm91cin">
        <motion.div initial={{
        opacity: 0,
        x: -30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="max-w-xl order-2 md:order-1" data-id="element-104" data-mp="3o6kh1mlj0k78">
          <h1 className="text-[1.65rem] sm:text-4xl md:text-4xl xl:text-5xl font-black text-warmBrown leading-[1.1] mb-6" data-id="element-105" data-mp="30gq5r9guv3p3">
            <span className="block whitespace-nowrap" data-id="element-106" data-mp="2qjv80ssep7bv">
              Your{' '}
              <span className="relative inline-grid text-peach" aria-live="polite" data-id="element-107" data-mp="2qhqudj4pgtye">
                <span className="invisible col-start-1 row-start-1" data-id="element-108" data-mp="2vjga3c2vhvst">stuffed animal.</span>
                <AnimatePresence mode="wait" data-id="element-109" data-mp="2hkgl23bg7ew4">
                  <motion.span key={subjectIndex} initial={{
                  y: 14,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} exit={{
                  y: -14,
                  opacity: 0
                }} transition={{
                  duration: 0.35,
                  ease: 'easeInOut'
                }} className="col-start-1 row-start-1 whitespace-nowrap" data-id="element-110" data-mp="2lkqwvwlx8ziw">
                    {subjects[subjectIndex]}.
                  </motion.span>
                </AnimatePresence>
              </span>
            </span>
            <span className="block" data-id="element-111" data-mp="1azumdhg3ytyy">Your self-care buddy.</span>
          </h1>

          <p className="text-lg md:text-xl text-warmBrown/80 mb-8 leading-relaxed" data-id="element-112" data-mp="161aznetoxe5j">
            Turn your real pet, childhood plushie, or favorite action figure into a cute digital companion. Keep them happy by
            completing daily habits, tracking your mood, and taking moments to
            breathe. Take care of yourself — together.
          </p>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-3" data-id="element-113" data-mp="3lquftrppid6n">
            <motion.a href="https://apps.apple.com/us/app/pawchi-self-care-pet/id6764858701" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="inline-block" aria-label="Download on the App Store" data-id="element-114" data-mp="3htgaib55cpjx">
              <img src="https://cdn.magicpatterns.com/uploads/bgW7hVXTEHmConMeGXzro4/app-store.svg" alt="Download on the App Store" className="h-[52px] w-auto" data-id="element-115" data-mp="2g02iwennzdwr" />
            </motion.a>
            <motion.a href="https://play.google.com/store/apps/details?id=app.pawchi.mobile" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="inline-block -ml-3 sm:ml-0" aria-label="Get it on Google Play" data-id="element-116" data-mp="2p10gae3lkn6d">
              <img src="https://cdn.magicpatterns.com/uploads/omQrJZxYBdQEgWZzBHqrye/play-store.png" alt="Get it on Google Play" className="h-[76px] w-auto -my-3" data-id="element-117" data-mp="3vhp3brhz62nc" />
            </motion.a>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="relative h-[380px] md:h-[600px] flex justify-center items-center order-1 md:order-2 mt-8 md:mt-0" data-id="element-118" data-mp="98mi3pan4tu3">
          {/* Floating elements */}
          <motion.div animate={{
          y: [-10, 10, -10]
        }} transition={{
          repeat: Infinity,
          duration: 4,
          ease: 'easeInOut'
        }} className="absolute top-20 left-10 text-peach" data-id="element-119" data-mp="2yz99nzdl4hgw">
            <Heart size={32} fill="currentColor" data-id="element-120" data-mp="1e9vwb9tpwyil" />
          </motion.div>
          <motion.div animate={{
          y: [10, -10, 10]
        }} transition={{
          repeat: Infinity,
          duration: 5,
          ease: 'easeInOut',
          delay: 1
        }} className="absolute bottom-32 right-10 text-lavender" data-id="element-121" data-mp="zkhjfrqxnyyg">
            <Star size={40} fill="currentColor" data-id="element-122" data-mp="1x8rjw2b2meng" />
          </motion.div>
          <motion.div animate={{
          y: [-15, 15, -15],
          rotate: [0, 10, 0]
        }} transition={{
          repeat: Infinity,
          duration: 6,
          ease: 'easeInOut',
          delay: 2
        }} className="absolute top-40 right-20 text-4xl" data-id="element-123" data-mp="1j8gw58525g6i">
            🐾
          </motion.div>

          {/* Phone Mockup */}
          <div className="relative z-[2] w-[220px] h-[440px] md:w-[300px] md:h-[600px] bg-warmBrown rounded-[2.2rem] md:rounded-[3rem] shadow-soft-lg border-[3px] border-warmBrown/20 p-[4px] md:p-[6px] overflow-hidden" data-id="element-124" data-mp="2ei3m9vqp7rau">
            {/* Video */}
            <video src="https://cdn.magicpatterns.com/uploads/uZzShn8uwKzbfKBmC7H8k2/Matte_2026-05-18_17-03-43.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover rounded-[2.6rem]" data-id="element-125" data-mp="24pxyyzbe5y1z" />
          </div>
        </motion.div>
      </div>
    </section>;
}